/**
 * One feed card (spec 9.2), ported from prototype A "Vollbild": the 960 px image full-bleed
 * behind a vertical gradient, text bottom-left with a shadow as a second safety, the header and
 * rail from `CardChrome`, and the credit "i" on the last card of the topic only (spec 9.4).
 */

import type { Card, Credit } from "../../shared/api-types.js";
import { CardRail, GoalRing, TopicLabel } from "./CardChrome.js";
import { CreditButton, CreditSheet } from "./CreditSheet.js";

export function CardScreen({
  card,
  totalCards,
  categoryLabel,
  topicTitle,
  today,
  dayGoal,
  credit,
  creditOpen,
  onOpenCredit,
  onCloseCredit,
  eager,
}: {
  card: Card;
  totalCards: number;
  categoryLabel: string;
  topicTitle: string;
  today: number;
  dayGoal: number;
  credit: Credit;
  creditOpen: boolean;
  onOpenCredit: () => void;
  onCloseCredit: () => void;
  /** The current card and its neighbour load eagerly; the rest wait (build spec 4.3). */
  eager: boolean;
}) {
  const isLastCard = card.position === totalCards;

  return (
    <section className="feed-screen">
      <img
        src={card.image.url}
        alt=""
        width={card.image.width}
        height={card.image.height}
        loading={eager ? "eager" : "lazy"}
        fetchPriority={eager ? "high" : "auto"}
        decoding="async"
        className="absolute inset-0 h-full w-full bg-[#111] object-cover"
      />
      <div className="card-shade absolute inset-0" />

      <TopicLabel category={categoryLabel} title={topicTitle} />
      <GoalRing today={today} dayGoal={dayGoal} />
      <CardRail total={totalCards} current={card.position} />

      <div
        className="absolute bottom-[44px] left-5 z-2"
        style={{ right: "64px", paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <h2 className="m-0 mb-2 text-[24px] leading-[1.05] font-extrabold tracking-[-.01em] text-white [text-shadow:0_2px_12px_rgba(0,0,0,.5)]">
          {card.title}
        </h2>
        <p className="m-0 text-[17px] leading-[1.4] text-white [text-shadow:0_1px_8px_rgba(0,0,0,.6)]">
          {card.body}
        </p>
      </div>

      {isLastCard && (
        <>
          <CreditButton onOpen={onOpenCredit} />
          <CreditSheet
            credit={credit}
            topicTitle={topicTitle}
            open={creditOpen}
            onClose={onCloseCredit}
          />
        </>
      )}
    </section>
  );
}
